import type { TableColumn } from "../../../models/ManualAuthoring";

export type HeaderCell = {
  id: string;
  header: string;
  colSpan: number;
  rowSpan: number;
  width?: number;
};

export function getLeafColumns(columns: TableColumn[]): TableColumn[] {
  let result: TableColumn[] = [];

  for (const col of columns) {
    if (col.columns?.length) {
      result = result.concat(getLeafColumns(col.columns));
    } else {
      result.push(col);
    }
  }

  return result;
}

function getColumnDepth(column: TableColumn): number {
  if (!column.columns?.length) return 1;
  return 1 + Math.max(...column.columns.map(getColumnDepth));
}

function getTreeDepth(columns: TableColumn[]): number {
  if (!columns.length) return 1;
  return Math.max(...columns.map(getColumnDepth));
}

function getLeafCount(column: TableColumn): number {
  if (!column.columns?.length) return 1;
  return column.columns.reduce((total, child) => total + getLeafCount(child), 0);
}

function getNormalizedSiblingWidths(columns: TableColumn[]): number[] {
  if (!columns.length) return [];

  const rawWidths = columns.map((column) => {
    const width = Number(column.width);
    return Number.isFinite(width) && width > 0 ? width : 0;
  });

  const total = rawWidths.reduce((sum, width) => sum + width, 0);
  if (total > 0) {
    return rawWidths.map((width) => (width / total) * 100);
  }

  const equal = 100 / columns.length;
  return columns.map(() => equal);
}

export function getLeafColumnWidthMap(
  columns: TableColumn[]
): Record<string, number> {
  const widthMap: Record<string, number> = {};

  function traverse(cols: TableColumn[], parentWidth: number) {
    if (!cols.length) return;

    const normalizedSiblingWidths = getNormalizedSiblingWidths(cols);

    cols.forEach((col, index) => {
      const effectiveWidth = (parentWidth * normalizedSiblingWidths[index]) / 100;

      if (col.columns?.length) {
        traverse(col.columns, effectiveWidth);
        return;
      }

      widthMap[col.id] = effectiveWidth;
    });
  }

  traverse(columns, 100);
  return widthMap;
}

export function buildHeaderRows(columns: TableColumn[]): HeaderCell[][] {
  const rows: HeaderCell[][] = [];
  const maxDepth = getTreeDepth(columns);

  function traverse(cols: TableColumn[], depth: number, parentWidth: number) {
    if (!rows[depth]) rows[depth] = [];

    const normalizedSiblingWidths = getNormalizedSiblingWidths(cols);

    cols.forEach((col, index) => {
      const hasChildren = !!col.columns?.length;
      const effectiveWidth = (parentWidth * normalizedSiblingWidths[index]) / 100;

      const cell: HeaderCell = {
        id: col.id,
        header: col.header || "Untitled",
        colSpan: hasChildren ? getLeafCount(col) : 1,
        rowSpan: hasChildren ? 1 : maxDepth - depth,
        width: effectiveWidth,
      };

      rows[depth].push(cell);

      if (hasChildren) {
        traverse(col.columns!, depth + 1, effectiveWidth);
      }
    });
  }

  traverse(columns, 0, 100);
  return rows;
}
